import api from './api';

// =============================================================================
// AUDIT LOG SERVICE
// =============================================================================
// Intercepts all admin actions, sends them to the backend API for persistent
// daily-file storage, and maintains an IndexedDB fallback for offline mode.
// Every log entry includes a human-readable summary for non-technical readers.
// =============================================================================

const DB_NAME = 'sulhafa_audit_logs';
const DB_VERSION = 1;
const STORE_NAME = 'logs';
const RETENTION_DAYS = 45;

// ---------------------------------------------------------------------------
// Action Types — machine key + human label + plain-language verb
// ---------------------------------------------------------------------------
export const ACTION_TYPES = {
  // User moderation
  USER_SUSPEND: { key: 'USER_SUSPEND', label: 'Suspended User', category: 'user' },
  USER_MUTE: { key: 'USER_MUTE', label: 'Muted User', category: 'user' },
  USER_BAN: { key: 'USER_BAN', label: 'Banned User', category: 'user' },
  USER_UNSUSPEND: { key: 'USER_UNSUSPEND', label: 'Unsuspended User', category: 'user' },
  USER_UNMUTE: { key: 'USER_UNMUTE', label: 'Unmuted User', category: 'user' },
  USER_RESTORE: { key: 'USER_RESTORE', label: 'Restored User', category: 'user' },
  USER_IGNORE_REPORTS: { key: 'USER_IGNORE_REPORTS', label: 'Ignored User Reports', category: 'user' },
  USER_DELETE: { key: 'USER_DELETE', label: 'Deleted User', category: 'user' },
  USER_PERMANENT_DELETE: { key: 'USER_PERMANENT_DELETE', label: 'Permanently Deleted User', category: 'user' },

  // Post moderation
  POST_DELETE: { key: 'POST_DELETE', label: 'Deleted Post', category: 'post' },
  POST_IGNORE_REPORTS: { key: 'POST_IGNORE_REPORTS', label: 'Ignored Post Reports', category: 'post' },

  // Clinic management
  CLINIC_VERIFY: { key: 'CLINIC_VERIFY', label: 'Verified Clinic', category: 'clinic' },
  CLINIC_REJECT: { key: 'CLINIC_REJECT', label: 'Rejected Clinic', category: 'clinic' },
  CLINIC_UPGRADE_SUB: { key: 'CLINIC_UPGRADE_SUB', label: 'Upgraded Clinic Subscription', category: 'clinic' },
  CLINIC_DOWNGRADE_SUB: { key: 'CLINIC_DOWNGRADE_SUB', label: 'Downgraded Clinic Subscription', category: 'clinic' },

  // System-wide
  SYSTEM_ANNOUNCEMENT: { key: 'SYSTEM_ANNOUNCEMENT', label: 'Sent Announcement', category: 'system' },
  SYSTEM_BULK_MESSAGE: { key: 'SYSTEM_BULK_MESSAGE', label: 'Sent Bulk Message', category: 'system' },
  APP_VERSION_UPDATE: { key: 'APP_VERSION_UPDATE', label: 'Updated App Version', category: 'system' },

  // CMS — Banners
  CMS_BANNER_CREATE: { key: 'CMS_BANNER_CREATE', label: 'Created Banner', category: 'cms' },
  CMS_BANNER_UPDATE: { key: 'CMS_BANNER_UPDATE', label: 'Updated Banner', category: 'cms' },
  CMS_BANNER_DELETE: { key: 'CMS_BANNER_DELETE', label: 'Deleted Banner', category: 'cms' },

  // CMS — Breeds
  CMS_BREED_CREATE: { key: 'CMS_BREED_CREATE', label: 'Created Breed', category: 'cms' },
  CMS_BREED_UPDATE: { key: 'CMS_BREED_UPDATE', label: 'Updated Breed', category: 'cms' },
  CMS_BREED_DELETE: { key: 'CMS_BREED_DELETE', label: 'Deleted Breed', category: 'cms' },
  CMS_BREED_REORDER: { key: 'CMS_BREED_REORDER', label: 'Reordered Breeds', category: 'cms' },

  // CMS — Species
  CMS_SPECIES_CREATE: { key: 'CMS_SPECIES_CREATE', label: 'Created Species', category: 'cms' },
  CMS_SPECIES_UPDATE: { key: 'CMS_SPECIES_UPDATE', label: 'Updated Species', category: 'cms' },
  CMS_SPECIES_DELETE: { key: 'CMS_SPECIES_DELETE', label: 'Deleted Species', category: 'cms' },
  CMS_SPECIES_REORDER: { key: 'CMS_SPECIES_REORDER', label: 'Reordered Species', category: 'cms' },

  // CMS — Advertisements
  CMS_AD_CREATE: { key: 'CMS_AD_CREATE', label: 'Created Advertisement', category: 'cms' },
  CMS_AD_UPDATE: { key: 'CMS_AD_UPDATE', label: 'Updated Advertisement', category: 'cms' },
  CMS_AD_DELETE: { key: 'CMS_AD_DELETE', label: 'Deleted Advertisement', category: 'cms' },
  CMS_AD_REORDER: { key: 'CMS_AD_REORDER', label: 'Reordered Advertisements', category: 'cms' },

  // Pet
  PET_DELETE: { key: 'PET_DELETE', label: 'Deleted Pet', category: 'pet' },

  // Data-entry — Species
  DATA_ENTRY_SPECIES_CREATE: { key: 'DATA_ENTRY_SPECIES_CREATE', label: 'Created Data-Entry Species', category: 'data-entry' },
  DATA_ENTRY_SPECIES_UPDATE: { key: 'DATA_ENTRY_SPECIES_UPDATE', label: 'Updated Data-Entry Species', category: 'data-entry' },
  DATA_ENTRY_SPECIES_DELETE: { key: 'DATA_ENTRY_SPECIES_DELETE', label: 'Deleted Data-Entry Species', category: 'data-entry' },

  // Data-entry — Breeds
  DATA_ENTRY_BREED_CREATE: { key: 'DATA_ENTRY_BREED_CREATE', label: 'Created Data-Entry Breed', category: 'data-entry' },
  DATA_ENTRY_BREED_UPDATE: { key: 'DATA_ENTRY_BREED_UPDATE', label: 'Updated Data-Entry Breed', category: 'data-entry' },
  DATA_ENTRY_BREED_DELETE: { key: 'DATA_ENTRY_BREED_DELETE', label: 'Deleted Data-Entry Breed', category: 'data-entry' },

  // Referral
  REFERRAL_CODE_SUSPEND: { key: 'REFERRAL_CODE_SUSPEND', label: 'Suspended Referral Code', category: 'referral' },

  // Clinic — visit deletions
  CLINIC_APPOINTMENT_DELETE: { key: 'CLINIC_APPOINTMENT_DELETE', label: 'Deleted Clinic Appointment', category: 'clinic' },
  CLINIC_OUTPATIENT_DELETE: { key: 'CLINIC_OUTPATIENT_DELETE', label: 'Deleted Outpatient Visit', category: 'clinic' },
  CLINIC_INPATIENT_DELETE: { key: 'CLINIC_INPATIENT_DELETE', label: 'Deleted Inpatient Visit', category: 'clinic' },
};

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------
let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('date', 'date', { unique: false });
        store.createIndex('actionType', 'actionType', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('adminId', 'adminId', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
    };
  });
  return dbPromise;
}

async function saveToIndexedDB(entry) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve(request.result);
    tx.onerror = () => reject(tx.error);
    const store = tx.objectStore(STORE_NAME);
    const request = store.add(entry);
  });
}

async function getFromIndexedDB(filters = {}) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      let logs = request.result || [];
      // Apply filters client-side
      if (filters.dateFrom) {
        logs = logs.filter(l => l.date >= filters.dateFrom);
      }
      if (filters.dateTo) {
        logs = logs.filter(l => l.date <= filters.dateTo);
      }
      if (filters.actionType && filters.actionType.length > 0) {
        logs = logs.filter(l => filters.actionType.includes(l.actionType));
      }
      if (filters.status) {
        logs = logs.filter(l => l.status === filters.status);
      }
      if (filters.search) {
        const q = filters.search.toLowerCase();
        logs = logs.filter(l =>
          (l.summary && l.summary.toLowerCase().includes(q)) ||
          (l.targetName && l.targetName.toLowerCase().includes(q)) ||
          (l.adminName && l.adminName.toLowerCase().includes(q)) ||
          (l.adminEmail && l.adminEmail.toLowerCase().includes(q)) ||
          (l.targetId && String(l.targetId).toLowerCase().includes(q))
        );
      }
      // Sort by timestamp desc
      logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      resolve(logs);
    };
    request.onerror = () => reject(request.error);
  });
}

async function getOfflineQueueCount() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch {
    return 0;
  }
}

async function deleteFromIndexedDB(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    const store = tx.objectStore(STORE_NAME);
    store.delete(id);
  });
}

async function cleanupOldLogs() {
  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - RETENTION_DAYS);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('date');
    const request = index.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.date < cutoffStr) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
  } catch (e) {
    console.warn('[AuditLog] Cleanup failed:', e);
  }
}

// ---------------------------------------------------------------------------
// Admin user helper
// ---------------------------------------------------------------------------
function getCurrentAdmin() {
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return null;
    const user = JSON.parse(raw);
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const displayName = user.display_name || '';
    const username = user.username || '';
    const name = (firstName + ' ' + lastName).trim() || displayName || username || 'Unknown Admin';
    return {
      id: user.id || user.user_id || user.pk || null,
      name,
      email: user.email || '',
      userType: user.user_type || user.userType || '',
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Summary builder — plain language for non-technical readers
// ---------------------------------------------------------------------------
function buildSummary(actionType, adminName, targetName, details = {}) {
  const admin = adminName || 'An admin';
  const target = targetName || 'a target';
  // For system-wide actions with no target, use friendlier wording
  const systemActions = [
    ACTION_TYPES.SYSTEM_ANNOUNCEMENT.key,
    ACTION_TYPES.SYSTEM_BULK_MESSAGE.key,
    ACTION_TYPES.APP_VERSION_UPDATE.key,
  ];
  const isSystemAction = systemActions.includes(actionType);
  const reason = details?.reason || (details?.reasons?.length ? details.reasons.join(', ') : '');
  const duration = details?.durationHours || details?.duration_hours || '';

  switch (actionType) {
    case ACTION_TYPES.USER_SUSPEND.key:
      return duration
        ? `${admin} suspended user ${target} for ${duration} hours.${reason ? ` Reason: ${reason}` : ''}`
        : `${admin} suspended user ${target}.${reason ? ` Reason: ${reason}` : ''}`;
    case ACTION_TYPES.USER_MUTE.key:
      return duration
        ? `${admin} muted user ${target} for ${duration} hours.${reason ? ` Reason: ${reason}` : ''}`
        : `${admin} muted user ${target}.${reason ? ` Reason: ${reason}` : ''}`;
    case ACTION_TYPES.USER_BAN.key:
      return `${admin} banned user ${target}.${reason ? ` Reason: ${reason}` : ''}`;
    case ACTION_TYPES.USER_UNSUSPEND.key:
      return `${admin} unsuspended user ${target}.`;
    case ACTION_TYPES.USER_UNMUTE.key:
      return `${admin} unmuted user ${target}.`;
    case ACTION_TYPES.USER_RESTORE.key:
      return `${admin} restored (unbanned) user ${target}.`;
    case ACTION_TYPES.USER_IGNORE_REPORTS.key:
      return `${admin} ignored reports on user ${target}.`;
    case ACTION_TYPES.USER_DELETE.key:
      return `${admin} deleted user ${target} (soft delete).`;
    case ACTION_TYPES.USER_PERMANENT_DELETE.key:
      return `${admin} permanently deleted user ${target}.`;
    case ACTION_TYPES.POST_DELETE.key:
      return `${admin} deleted a post by ${target}.${reason ? ` Reason: ${reason}` : ''}`;
    case ACTION_TYPES.POST_IGNORE_REPORTS.key:
      return `${admin} ignored reports on a post by ${target}.`;
    case ACTION_TYPES.CLINIC_VERIFY.key:
      return `${admin} verified clinic ${target}.`;
    case ACTION_TYPES.CLINIC_REJECT.key:
      return `${admin} rejected clinic ${target}.${reason ? ` Reason: ${reason}` : ''}`;
    case ACTION_TYPES.CLINIC_UPGRADE_SUB.key:
      return `${admin} upgraded clinic ${target} to a paid subscription.`;
    case ACTION_TYPES.CLINIC_DOWNGRADE_SUB.key:
      return `${admin} downgraded clinic ${target} to a free subscription.`;
    case ACTION_TYPES.SYSTEM_ANNOUNCEMENT.key:
      return `${admin} sent a system-wide announcement to all users.`;
    case ACTION_TYPES.SYSTEM_BULK_MESSAGE.key:
      return `${admin} sent a bulk message to all users.`;
    case ACTION_TYPES.APP_VERSION_UPDATE.key:
      return `${admin} updated the app version information.`;
    case ACTION_TYPES.CMS_BANNER_CREATE.key:
      return `${admin} created a new app banner.`;
    case ACTION_TYPES.CMS_BANNER_UPDATE.key:
      return `${admin} updated app banner "${target}".`;
    case ACTION_TYPES.CMS_BANNER_DELETE.key:
      return `${admin} deleted app banner "${target}".`;
    case ACTION_TYPES.CMS_BREED_CREATE.key:
      return `${admin} created a new breed.`;
    case ACTION_TYPES.CMS_BREED_UPDATE.key:
      return `${admin} updated breed "${target}".`;
    case ACTION_TYPES.CMS_BREED_DELETE.key:
      return `${admin} deleted breed "${target}".`;
    case ACTION_TYPES.CMS_BREED_REORDER.key:
      return `${admin} reordered the breed list.`;
    case ACTION_TYPES.CMS_SPECIES_CREATE.key:
      return `${admin} created a new species.`;
    case ACTION_TYPES.CMS_SPECIES_UPDATE.key:
      return `${admin} updated species "${target}".`;
    case ACTION_TYPES.CMS_SPECIES_DELETE.key:
      return `${admin} deleted species "${target}".`;
    case ACTION_TYPES.CMS_SPECIES_REORDER.key:
      return `${admin} reordered the species list.`;
    case ACTION_TYPES.CMS_AD_CREATE.key:
      return `${admin} created a new advertisement.`;
    case ACTION_TYPES.CMS_AD_UPDATE.key:
      return `${admin} updated advertisement "${target}".`;
    case ACTION_TYPES.CMS_AD_DELETE.key:
      return `${admin} deleted advertisement "${target}".`;
    case ACTION_TYPES.CMS_AD_REORDER.key:
      return `${admin} reordered the advertisement list.`;
    case ACTION_TYPES.PET_DELETE.key:
      return `${admin} deleted pet "${target}".`;
    case ACTION_TYPES.DATA_ENTRY_SPECIES_CREATE.key:
      return `${admin} created a new data-entry species.`;
    case ACTION_TYPES.DATA_ENTRY_SPECIES_UPDATE.key:
      return `${admin} updated data-entry species "${target}".`;
    case ACTION_TYPES.DATA_ENTRY_SPECIES_DELETE.key:
      return `${admin} deleted data-entry species "${target}".`;
    case ACTION_TYPES.DATA_ENTRY_BREED_CREATE.key:
      return `${admin} created a new data-entry breed.`;
    case ACTION_TYPES.DATA_ENTRY_BREED_UPDATE.key:
      return `${admin} updated data-entry breed "${target}".`;
    case ACTION_TYPES.DATA_ENTRY_BREED_DELETE.key:
      return `${admin} deleted data-entry breed "${target}".`;
    case ACTION_TYPES.REFERRAL_CODE_SUSPEND.key:
      return `${admin} suspended referral code "${target}".${reason ? ` Reason: ${reason}` : ''}`;
    case ACTION_TYPES.CLINIC_APPOINTMENT_DELETE.key:
      return typeof details?.count === 'number' && details.count > 1
        ? `${admin} deleted ${details.count} clinic appointments.`
        : `${admin} deleted clinic appointment "${target}".`;
    case ACTION_TYPES.CLINIC_OUTPATIENT_DELETE.key:
      return typeof details?.count === 'number' && details.count > 1
        ? `${admin} deleted ${details.count} outpatient visits.`
        : `${admin} deleted outpatient visit "${target}".`;
    case ACTION_TYPES.CLINIC_INPATIENT_DELETE.key:
      return typeof details?.count === 'number' && details.count > 1
        ? `${admin} deleted ${details.count} inpatient visits.`
        : `${admin} deleted inpatient visit "${target}".`;
    default:
      return `${admin} performed action ${actionType} on ${target}.`;
  }
}

// ---------------------------------------------------------------------------
// Core log action
// ---------------------------------------------------------------------------
export async function logAction({
  actionType,
  targetId = null,
  targetName = null,
  targetType = 'unknown',
  details = {},
  status = 'success',
  errorMessage = null,
  durationMs = 0,
}) {
  const admin = getCurrentAdmin();
  const now = new Date();
  const date = now.toISOString().split('T')[0];
  const timestamp = now.toISOString();
  const actionMeta = ACTION_TYPES[actionType] || { label: actionType, category: 'unknown' };
  const summary = buildSummary(actionType, admin?.name, targetName, details);

  const effectiveTargetName = targetName
    || (targetId && targetType && targetType !== 'system' && targetType !== 'unknown'
        ? `${targetType} #${targetId}`
        : null);

  const entry = {
    timestamp,
    date,
    actionType,
    actionLabel: actionMeta.label,
    category: actionMeta.category,
    adminId: admin?.id || null,
    adminName: admin?.name || 'Unknown',
    adminEmail: admin?.email || '',
    targetType,
    targetId: targetId ? String(targetId) : null,
    targetName: effectiveTargetName,
    summary,
    details,
    status,
    errorMessage,
    durationMs,
    ipAddress: '', // Best effort below
    userAgent: navigator.userAgent || '',
  };

  // Try to capture IP from a meta tag if available (some setups expose it)
  const ipMeta = document.querySelector('meta[name="client-ip"]');
  if (ipMeta) entry.ipAddress = ipMeta.content;

  // 1. Try backend API first
  try {
    await api.post('/api/v1/admin/audit-logs', entry);
    // On success, run cleanup occasionally (1% chance)
    if (Math.random() < 0.01) cleanupOldLogs();
    return { success: true, source: 'api' };
  } catch {
    // 2. Fallback to IndexedDB
    try {
      await saveToIndexedDB(entry);
      return { success: true, source: 'indexeddb' };
    } catch (dbError) {
      console.error('[AuditLog] Failed to save log:', dbError);
      return { success: false, error: dbError.message };
    }
  }
}

// ---------------------------------------------------------------------------
// Retrieve logs (merge API + IndexedDB)
// ---------------------------------------------------------------------------
export async function getLogs(filters = {}) {
  const page = filters.page || 1;
  const perPage = filters.perPage || 25;

  // Try backend first — server-side pagination is the source of truth
  try {
    const params = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.actionType && filters.actionType.length) params.action_type = filters.actionType.join(',');
    if (filters.status) params.status = filters.status;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.perPage) params.per_page = filters.perPage;

    const response = await api.get('/api/v1/admin/audit-logs', { params });
    const data = response?.data?.data || response?.data || {};
    const apiLogs = data.logs || [];
    const apiTotal = data.total || 0;
    const apiCurrentPage = data.current_page || page;
    const successCount = typeof data.success_count === 'number' ? data.success_count : null;
    const errorCount = typeof data.error_count === 'number' ? data.error_count : null;

    // Unsynced local logs are prepended to page 1 only
    let localLogs = [];
    try {
      localLogs = await getFromIndexedDB(filters);
    } catch {
      // Ignore IndexedDB errors
    }

    const logs = page === 1 ? [...localLogs, ...apiLogs] : apiLogs;
    const total = apiTotal + localLogs.length;

    return {
      logs,
      total,
      currentPage: apiCurrentPage,
      lastPage: Math.ceil(total / perPage) || 1,
      source: localLogs.length > 0 ? 'merged' : 'api',
      successCount,
      errorCount,
      categoryCounts: data.category_counts || null,
    };
  } catch {
    // API failed — offline mode: full client-side pagination over IndexedDB
    let localLogs = [];
    try {
      localLogs = await getFromIndexedDB(filters);
    } catch {
      // Ignore IndexedDB errors
    }

    const total = localLogs.length;
    const start = (page - 1) * perPage;
    const paginated = localLogs.slice(start, start + perPage);

    return {
      logs: paginated,
      total,
      currentPage: page,
      lastPage: Math.ceil(total / perPage) || 1,
      source: 'indexeddb',
      successCount: null,
      errorCount: null,
    };
  }
}

// ---------------------------------------------------------------------------
// Export filtered logs as downloadable PDF (server-generated)
// ---------------------------------------------------------------------------
export async function exportPdf(filters = {}) {
  const params = {};
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.actionType && filters.actionType.length) params.action_type = filters.actionType.join(',');
  if (filters.status) params.status = filters.status;
  if (filters.search) params.search = filters.search;

  const response = await api.get('/api/v1/admin/audit-logs/export-pdf', {
    params,
    responseType: 'blob',
  });

  const disposition = response.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match
    ? match[1]
    : `sulhafa-audit-log-${new Date().toISOString().split('T')[0]}.pdf`;

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return { success: true };
}

// ---------------------------------------------------------------------------
// Sync offline logs to backend
// ---------------------------------------------------------------------------
export async function syncOfflineLogs() {
  try {
    const db = await openDB();
    const allLogs = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

    let synced = 0;
    let failed = 0;
    for (const log of allLogs) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post('/api/v1/admin/audit-logs', log);
        // eslint-disable-next-line no-await-in-loop
        await deleteFromIndexedDB(log.id);
        synced++;
      } catch (err) {
        failed++;
        console.warn('[AuditLog] Sync failed for log:', log.id, err?.message || err);
      }
    }

    // Re-count from DB to be certain
    const remaining = await getOfflineQueueCount();
    console.log('[AuditLog] Sync complete:', { synced, failed, remaining });
    return { success: true, synced, remaining };
  } catch (e) {
    console.error('[AuditLog] Sync error:', e);
    return { success: false, error: e.message };
  }
}

// ---------------------------------------------------------------------------
// withAuditLog higher-order function
// ---------------------------------------------------------------------------
export function withAuditLog(actionType, fn, metadataExtractor) {
  return async function (...args) {
    const startTime = performance.now();
    try {
      const result = await fn.apply(this, args);
      const durationMs = Math.round(performance.now() - startTime);
      const meta = metadataExtractor ? metadataExtractor(...args, result) : {};
      // Fire-and-forget logging
      logAction({
        actionType: meta.actionType || ACTION_TYPES[actionType]?.key || actionType,
        targetId: meta.targetId ?? null,
        targetName: meta.targetName ?? null,
        targetType: meta.targetType ?? 'unknown',
        details: meta.details ?? {},
        status: 'success',
        durationMs,
      }).catch(() => {});
      return result;
    } catch (error) {
      const durationMs = Math.round(performance.now() - startTime);
      const meta = metadataExtractor ? metadataExtractor(...args) : {};
      logAction({
        actionType: meta.actionType || ACTION_TYPES[actionType]?.key || actionType,
        targetId: meta.targetId ?? null,
        targetName: meta.targetName ?? null,
        targetType: meta.targetType ?? 'unknown',
        details: meta.details ?? {},
        status: 'error',
        errorMessage: error?.message || error?.response?.data?.message || 'Unknown error',
        durationMs,
      }).catch(() => {});
      throw error;
    }
  };
}

// ---------------------------------------------------------------------------
// Utility: get offline queue count
// ---------------------------------------------------------------------------
export { getOfflineQueueCount };

// Run cleanup on module load (best effort)
cleanupOldLogs().catch(() => {});
